import React, { useState, useEffect } from 'react';
import { MapPin, Calendar, Users, Clock, Info, X, ExternalLink, Search, Send, Plus, ArrowRight, Mail } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import Logo from '../../components/Logo';
import Footer from '../../components/Footer';
import WalletMenu from '../../components/WalletMenu';
import { supabase } from '../../lib/supabase';
import NavigationMenu from '../../components/NavigationMenu';

interface TravelService {
  id: string;
  title: string;
  description: string;
  imageUrl: string;
  link: string;
  category: string;
}

export default function AITravelAgent() {
  const { isAuthenticated, user } = useAuth();
  const [services, setServices] = useState<TravelService[]>([
    {
      id: '1',
      title: 'Private Jet Charter',
      description: 'Experience the ultimate in luxury air travel with our private jet charter services.',
      imageUrl: 'https://images.unsplash.com/photo-1540962351504-03099e0a754b?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2574&q=80',
      link: '/services/private-jet-charter',
      category: 'air'
    },
    {
      id: '2',
      title: 'Yacht Charter',
      description: 'Explore the world\'s most beautiful coastlines with our luxury yacht charter services.',
      imageUrl: 'https://images.unsplash.com/photo-1567899378494-47b22a2ae96a?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2340&q=80',
      link: '/services/yacht-charter',
      category: 'sea'
    },
    {
      id: '3',
      title: 'Helicopter Charter',
      description: 'Reach your destination quickly and efficiently with our premium helicopter services.',
      imageUrl: 'https://sgfnbormqiqgvhdfwmhz.supabase.co/storage/v1/object/sign/helicopter/image-112-1024x683.png?token=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1cmwiOiJoZWxpY29wdGVyL2ltYWdlLTExMi0xMDI0eDY4My5wbmciLCJpYXQiOjE3NDY0Njg2MzIsImV4cCI6MzA0MjQ2ODYzMn0.bQOoPWMGDAh8EeRCHYQFZMiBhkQ1RD9osvUpGVdoy9M',
      link: '/services/helicopter-charter',
      category: 'air'
    },
    {
      id: '4',
      title: 'Luxury Car Rental',
      description: 'Drive in style with our premium luxury car rental services.',
      imageUrl: 'https://images.unsplash.com/photo-1503376780353-7e6692767b70?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2340&q=80',
      link: '/luxury-cars',
      category: 'land'
    },
    {
      id: '5',
      title: 'Empty Leg Flights',
      description: 'Take advantage of exclusive empty leg flights at significantly reduced prices.',
      imageUrl: 'https://images.unsplash.com/photo-1494412651409-8963ce7935a7?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2340&q=80',
      link: '/empty-legs',
      category: 'air'
    },
    {
      id: '6',
      title: 'Fixed Offers',
      description: 'Discover our curated selection of fixed-price private jet packages.',
      imageUrl: 'https://images.unsplash.com/photo-1499856871958-5b9627545d1a?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2340&q=80',
      link: '/fixed-offers',
      category: 'air'
    }
  ]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [emailSubscription, setEmailSubscription] = useState('');
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);

  useEffect(() => {
    // Simulate loading
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
    }, 1000);
  }, []);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!emailSubscription) return;
    
    setIsSubscribing(true);
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // In a real application, you would send this to your backend
      console.log('Subscribing email to AI Travel Agent launch:', emailSubscription);
      
      setIsSubscribed(true);
      setEmailSubscription('');
      
      // Reset after 3 seconds
      setTimeout(() => {
        setIsSubscribed(false);
      }, 3000);
    } catch (error) {
      console.error('Error subscribing:', error);
    } finally {
      setIsSubscribing(false);
    }
  };

  const filteredServices = services.filter(service => {
    // Apply search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      return (
        service.title.toLowerCase().includes(search) ||
        service.description.toLowerCase().includes(search) ||
        service.category.toLowerCase().includes(search)
      );
    }
    
    // Apply category filter
    if (filterCategory !== 'all') {
      return service.category.toLowerCase() === filterCategory.toLowerCase();
    }
    
    return true;
  });

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-b from-gray-50 to-white border-b border-gray-100">
        <div className="max-w-8xl mx-auto">
          <div className="flex items-center justify-between py-4 px-6">
            <div className="flex items-center gap-8">
              <Logo />
              <NavigationMenu />
            </div>
            <div className="flex items-center gap-4">
              <WalletMenu onShowDashboard={() => {}} />
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 pt-[88px]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          {/* Hero Section */}
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">AI Travel Agent</h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Experience the future of luxury travel planning with our AI-powered travel agent. Personalized recommendations, instant responses, and seamless booking.
            </p>
          </div>

          {/* Search Bar */}
          <div className="mb-8">
            <div className="max-w-md mx-auto">
              <div className="relative">
                <Search size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search travel services..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent shadow-sm"
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X size={16} />
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap justify-center gap-2 mb-8">
            <button
              onClick={() => setFilterCategory('all')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filterCategory === 'all' ? 'bg-black text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              All Services
            </button>
            <button
              onClick={() => setFilterCategory('air')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filterCategory === 'air' ? 'bg-black text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Air Travel
            </button>
            <button
              onClick={() => setFilterCategory('sea')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filterCategory === 'sea' ? 'bg-black text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Sea Travel
            </button>
            <button
              onClick={() => setFilterCategory('land')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filterCategory === 'land' ? 'bg-black text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Land Travel
            </button>
          </div>

          {/* Services Grid */}
          {loading ? (
            <div className="flex justify-center items-center py-20">
              <div className="w-12 h-12 border-4 border-gray-200 border-t-black rounded-full animate-spin"></div>
            </div>
          ) : filteredServices.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredServices.map((service) => (
                <a 
                  key={service.id} 
                  href={service.link}
                  className="bg-white rounded-xl overflow-hidden shadow-md hover:shadow-lg transition-shadow border border-gray-100 cursor-pointer group"
                >
                  <div className="relative h-48 overflow-hidden">
                    <img 
                      src={service.imageUrl} 
                      alt={service.title} 
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                    <div className="absolute bottom-4 left-4 right-4">
                      <h3 className="text-xl font-bold text-white mb-1">{service.title}</h3>
                      <div className="flex items-center text-white/80 text-sm">
                        <ArrowRight size={14} className="mr-1" />
                        <span>Explore service</span>
                      </div>
                    </div>
                  </div>
                  <div className="p-5">
                    <p className="text-gray-600 mb-4">{service.description}</p>
                    <div className="flex justify-end">
                      <span className="text-sm bg-black text-white px-3 py-1.5 rounded-lg font-medium group-hover:bg-gray-800 transition-colors">
                        View Details
                      </span>
                    </div>
                  </div>
                </a>
              ))}
            </div>
          ) : (
            <div className="text-center py-16 bg-gray-50 rounded-xl">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Info size={24} className="text-gray-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">No services found</h3>
              <p className="text-gray-500 mt-2">
                No travel services match your search criteria. Please try different search terms or filters.
              </p>
            </div>
          )}

          {/* AI Travel Agent Coming Soon Section */}
          <div className="mt-16 bg-gray-100 rounded-xl p-8">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-2xl font-bold mb-4">AI Travel Agent Coming Soon</h2>
              <p className="text-gray-600 mb-6">
                Our AI-powered travel agent is currently in development. Sign up to be notified when it launches and get exclusive early access.
              </p>
              
              <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
                <div className="relative flex-1">
                  <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="email"
                    placeholder="Enter your email address"
                    value={emailSubscription}
                    onChange={(e) => setEmailSubscription(e.target.value)}
                    required
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                  />
                </div>
                <button
                  type="submit"
                  disabled={isSubscribing}
                  className="bg-black text-white px-6 py-3 rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {isSubscribing ? (
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  ) : (
                    'Notify Me'
                  )}
                </button>
              </form>
              
              {isSubscribed && (
                <div className="mt-4 bg-green-50 text-green-700 p-3 rounded-lg inline-flex items-center gap-2">
                  <Check size={18} />
                  <span>Thank you! We'll notify you when the AI Travel Agent launches.</span>
                </div>
              )}
            </div>
          </div>

          {/* Features Section */}
          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white p-6 rounded-xl shadow-md border border-gray-100">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-blue-600">
                  <circle cx="12" cy="12" r="10"></circle>
                  <path d="M12 16v-4"></path>
                  <path d="M12 8h.01"></path>
                </svg>
              </div>
              <h3 className="text-xl font-bold mb-2">Personalized Recommendations</h3>
              <p className="text-gray-600">
                Our AI analyzes your preferences and travel history to provide tailored recommendations for your next journey.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-xl shadow-md border border-gray-100">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-green-600">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
                </svg>
              </div>
              <h3 className="text-xl font-bold mb-2">24/7 Assistance</h3>
              <p className="text-gray-600">
                Get instant support and answers to your travel questions any time of day, with our AI-powered assistant.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-xl shadow-md border border-gray-100">
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-purple-600">
                  <path d="M20.24 12.24a6 6 0 0 0-8.49-8.49L5 10.5V19h8.5z"></path>
                  <line x1="16" y1="8" x2="2" y2="22"></line>
                  <line x1="17.5" y1="15" x2="9" y2="15"></line>
                </svg>
              </div>
              <h3 className="text-xl font-bold mb-2">Seamless Booking</h3>
              <p className="text-gray-600">
                Book your entire journey with a few simple commands, from flights and accommodations to activities and dining.
              </p>
            </div>
          </div>

          {/* CTA Section */}
          <div className="bg-black text-white p-12 rounded-2xl text-center mt-16">
            <h2 className="text-3xl font-bold mb-4">Ready to Experience the Future of Travel?</h2>
            <p className="text-xl mb-8 max-w-2xl mx-auto">
              Explore our current services while we put the finishing touches on our AI Travel Agent. Sign up above to be the first to know when it launches.
            </p>
            <a 
              href="/"
              className="inline-flex items-center gap-2 bg-white text-black px-8 py-3 rounded-lg font-medium hover:bg-gray-100 transition-colors"
            >
              Explore Services <ArrowRight size={18} />
            </a>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}